/**
 * ShowingsTab — showings list + schedule/add modals.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 *
 * Previously the showings query fired unconditionally on page mount.
 * Now lazy — gated on isActive so it only fires when this tab is open.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { badgeVariants } from '../../../components/ui/badge-variants';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePropertyTimezone } from '../../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import {
  fetchShowingsForListing,
  updateAttendee,
} from '../../../lib/leasing.api';
import type { LeadDetail, ShowingAppointment, RsvpStatus } from './shared';
import {
  KIND_LABELS_LEAD,
  KIND_COLORS_LEAD,
  OUTCOME_LABELS_LEAD,
  OUTCOME_COLORS_LEAD,
  RSVP_LABELS_LEAD,
} from './shared-constants';
import { LeadSchedulePrivateModal } from './modals/LeadSchedulePrivateModal';
import { LeadAddToOpenHouseModal } from './modals/LeadAddToOpenHouseModal';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ShowingsTabProps {
  lead: LeadDetail;
  onRefresh: () => void;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// LeadShowingsSection (local)
// ---------------------------------------------------------------------------

interface LeadShowingsSectionProps {
  lead: LeadDetail;
  onRefresh: () => void;
  isActive: boolean;
}

function LeadShowingsSection({ lead, onRefresh, isActive }: LeadShowingsSectionProps) {
  const queryClient = useQueryClient();
  const propertyTimezone = usePropertyTimezone();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAddToOpenHouseModal, setShowAddToOpenHouseModal] = useState(false);
  const [editingAttendeeId, setEditingAttendeeId] = useState<string | null>(null);
  const [editingRsvp, setEditingRsvp] = useState<RsvpStatus>('invited');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Previously fetched unconditionally; now lazy — tab must be active.
  const { data: showingsData, isLoading, error } = useQuery({
    queryKey: ['listing', lead.listingId, 'showings', 'for-lead', lead.id],
    queryFn: async () => {
      const result = await fetchShowingsForListing(lead.listingId);
      // Filter to showings where this lead is an attendee
      const filtered = result.items.filter((appt) =>
        appt.attendees.some((a) => a.leadId === lead.id)
      );
      return { items: filtered, totalCount: filtered.length };
    },
    enabled: isActive,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['listing', lead.listingId, 'showings', 'for-lead', lead.id] });
  };

  const handleRsvpUpdate = async (appt: ShowingAppointment) => {
    const attendee = appt.attendees.find((a) => a.leadId === lead.id);
    if (!attendee) return;
    setIsUpdating(true);
    setUpdateError(null);
    try {
      await updateAttendee(lead.listingId, appt.id, attendee.id, { rsvpStatus: editingRsvp });
      setEditingAttendeeId(null);
      invalidate();
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update RSVP');
    } finally {
      setIsUpdating(false);
    }
  };

  const appointments = showingsData?.items ?? [];

  return (
    <section data-testid="lead-showings-section" className="mb-6 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Showings</h2>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="lead-add-to-open-house-button"
            className="min-h-[44px]"
            onClick={() => setShowAddToOpenHouseModal(true)}
          >
            + Add to Open House
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="lead-schedule-private-showing-button"
            className="min-h-[44px]"
            onClick={() => setShowScheduleModal(true)}
          >
            + Private Showing
          </Button>
        </div>
      </div>

      {isLoading && <Skeleton className="h-20 w-full" />}

      {error && (
        <div
          data-testid="lead-showings-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm"
          role="alert"
        >
          {error instanceof Error ? error.message : 'Failed to load showings'}
        </div>
      )}

      {updateError && (
        <div
          data-testid="lead-showings-update-error"
          className="p-2 mb-2 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm"
          role="alert"
        >
          {updateError}
        </div>
      )}

      {!isLoading && !error && appointments.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No showings scheduled for this lead.</p>
      )}

      {appointments.length > 0 && (
        <div className="space-y-2" data-testid="lead-showings-list">
          {appointments.map((appt) => {
            const myAttendee = appt.attendees.find((a) => a.leadId === lead.id);
            return (
              <div
                key={appt.id}
                data-testid={`lead-showing-row-${appt.id}`}
                className="rounded-md border border-border px-3 py-2.5 text-sm"
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium">{formatTimestamp(asIsoDateTimeString(appt.scheduledFor), propertyTimezone)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {appt.durationMinutes} min
                      {appt.location && ` · ${appt.location}`}
                    </p>
                    {myAttendee && (
                      <p className="text-xs text-muted-foreground">
                        RSVP: <span className="font-medium">{RSVP_LABELS_LEAD[myAttendee.rsvpStatus]}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                    <span
                      className={badgeVariants({ variant: KIND_COLORS_LEAD[appt.kind] })}
                    >
                      {KIND_LABELS_LEAD[appt.kind]}
                    </span>
                    <span
                      className={badgeVariants({ variant: OUTCOME_COLORS_LEAD[appt.outcome] })}
                    >
                      {OUTCOME_LABELS_LEAD[appt.outcome]}
                    </span>
                    {myAttendee && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-[44px]"
                        data-testid={`lead-showing-update-rsvp-${appt.id}`}
                        onClick={() => {
                          setEditingAttendeeId(appt.id);
                          setEditingRsvp(myAttendee.rsvpStatus);
                          setUpdateError(null);
                        }}
                      >
                        Update RSVP
                      </Button>
                    )}
                  </div>
                </div>

                {/* Inline RSVP editor */}
                {editingAttendeeId === appt.id && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`lead-rsvp-editor-${appt.id}`}>
                    <select
                      data-testid={`lead-rsvp-select-${appt.id}`}
                      aria-label="RSVP status"
                      value={editingRsvp}
                      onChange={(e) => setEditingRsvp(e.target.value as RsvpStatus)}
                      className="border border-input rounded px-2 py-1 text-xs bg-background text-foreground"
                      disabled={isUpdating}
                    >
                      {(['invited', 'confirmed', 'declined', 'attended', 'no_show'] as RsvpStatus[]).map((s) => (
                        <option key={s} value={s}>{RSVP_LABELS_LEAD[s]}</option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      className="min-h-[44px]"
                      data-testid={`lead-rsvp-save-${appt.id}`}
                      disabled={isUpdating}
                      onClick={() => void handleRsvpUpdate(appt)}
                    >
                      {isUpdating ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="min-h-[44px]"
                      data-testid={`lead-rsvp-cancel-${appt.id}`}
                      onClick={() => setEditingAttendeeId(null)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule private showing modal */}
      {showScheduleModal && (
        <LeadSchedulePrivateModal
          lead={lead}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={() => { invalidate(); onRefresh(); }}
        />
      )}

      {/* Add to open house modal */}
      {showAddToOpenHouseModal && (
        <LeadAddToOpenHouseModal
          lead={lead}
          onClose={() => setShowAddToOpenHouseModal(false)}
          onSuccess={() => { invalidate(); }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ShowingsTab
// ---------------------------------------------------------------------------

export function ShowingsTab({ lead, onRefresh, isActive }: ShowingsTabProps) {
  return (
    <div>
      <LeadShowingsSection lead={lead} onRefresh={onRefresh} isActive={isActive} />
    </div>
  );
}
